import ApiService from '../api.service';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';

/**
 * Deciding what happens to waiting requests.
 *
 * Reading them is a Firestore query; changing one is not. Every decision books
 * a job, sends an email or closes a record, so it goes through a function that
 * checks the caller's role and the request's current state.
 */

export type ReviewDecision = 'accept' | 'decline';

export interface IRequestDecision {
  uid: string;
  decision: ReviewDecision;
  reason?: string;
}

export interface IDecisionResult {
  uid: string;
  status?: string;
  error?: string;
}

export interface IAddressCorrection {
  uid: string;
  at: 'collection' | 'destination';
  site: string;
  location?: string;
  postcode?: string;
}

const DeliveryRequestsApi = {
  /**
   * Corrects an address that could not be placed.
   *
   * Saved straight away rather than held until the run is: whoever is looking
   * at the route is the person who knows where the ward is, and a correction
   * kept in the browser is lost the moment they close the dialog.
   */
  correctAddress: (correction: IAddressCorrection): Promise<unknown> =>
    ApiService.callFunction<unknown>(correction, FirebaseFunctionNames.CORRECT_DELIVERY_REQUEST_ADDRESS),

  /**
   * Puts a request in the holding area, or changes what it is marked as.
   *
   * A mark is a decision made but not carried out, so it belongs on the
   * request and not in this browser: it has to survive navigating away, and
   * anyone else working the queue has to see it.
   */
  mark: (uid: string, decision: ReviewDecision, reason?: string): Promise<unknown> =>
    ApiService.callFunction<unknown>(
      { uid, decision, ...(reason ? { reason } : {}) },
      FirebaseFunctionNames.MARK_DELIVERY_REQUEST,
    ),

  /** Takes a request back out of the holding area, deciding nothing. */
  clearMark: (uid: string): Promise<unknown> =>
    ApiService.callFunction<unknown>({ uid }, FirebaseFunctionNames.CLEAR_DELIVERY_REQUEST_MARK),

  /**
   * Always a set. One decision is a set of one, so gaining batching never
   * meant rebuilding this.
   *
   * `buildRun` sequences whatever booked into a run rather than putting it
   * straight in front of drivers. Extra 4 has no screen of its own — the queue
   * that already waits for review is the holding area.
   */
  commitDecisions: (
    decisions: IRequestDecision[],
    buildRun = false,
    /** Who the run goes to. Left out, it is posted for any driver to accept. */
    giveTo?: { uid: string; fullName: string },
  ): Promise<{ results: IDecisionResult[]; group?: { uid: string; reference: string; label: string }; groupError?: string }> =>
    ApiService.callFunction<{ results: IDecisionResult[]; group?: { uid: string; reference: string; label: string }; groupError?: string }>(
      { decisions, buildRun, ...(giveTo ? { giveTo } : {}) },
      FirebaseFunctionNames.COMMIT_DELIVERY_REQUEST_DECISIONS,
    ),
};

export default DeliveryRequestsApi;
